import myTraderaItemActions from '../state/actions/my-tradera-items';
import dayjs from 'dayjs';
import featureToggles from '../utils/feature-toggles';
import purchaseOrderActions from '../state/actions/purchase-order';
import {
    openNewMessageModal,
    openModal,
    openShareModal
} from 'tradera/state/ui/reducer';
import { types } from './item-actions-helper';
import { isNormalListViewType } from './item-helper';
import { SINGLE_FEEDBACK_DIALOG_ID } from 'tradera/apps/my-tradera/app/components/leave-feedback/constants';
import { toLocalizedUrl } from 'tradera/utils/url';
import {
    openChangeShipping,
    openSetDiscount,
    openShippingQrCodeModal
} from 'tradera/apps/my-tradera/app/state/reducers/my-tradera';
import { isStampProvider } from '../data/shipping-providers';
import { PaidStatuses } from 'tradera/constants/paid-statuses';
import { SHIPPING_PROVIDERS } from 'tradera/components/shipping-product/shipping-providers';
import { getSrc } from 'tradera/components/item-card/item-card-parts/item-card-image-size';
import { getMessagingPageLink } from 'tradera/apps/my-tradera/app/helpers/messaging-helpers';
import {
    isDenmarkCountryCode,
    isNetherlandsCountryCode,
    isSwedenCountryCodeOrUndefined
} from 'tradera/localization/countrycode';

const getId = (item) => item.key;
const getIds = (item) => [item.itemId];

const isTakeAway = (item) => {
    return (
        (item.transaction.selectedShipping &&
            item.transaction.selectedShipping.isTakeaway) ||
        (item.transaction.shippingDiscount &&
            item.transaction.shippingDiscount.isTakeaway)
    );
};

const hasStampsAsSelectedShippingType = (item) => {
    return (
        item.transaction.selectedShipping &&
        isStampProvider(item.transaction.selectedShipping.shippingType)
    );
};

// isPrimaryAction = will be primary action unless one before has not already been set as primary action
// Hence, the order of the actions matter.
const commonActions = (item) => ({
    sellSimilar: () => ({
        id: getId(item),
        type: types.LINK,
        label: 'Sälj liknande',
        getTranslatedLabel: (t) => t('myt_itemCard_actions_sellSimilar'),
        url: `/selling/sell-similar/${item.itemId}`,
        gaLabel: 'SellSimilar',
        isVisible: () => true,
        isPrimaryAction: () => true
    })
});

const hasTransactionDateNewerThen60Days = (item) => {
    const dateLimit = dayjs().subtract(60, 'day');
    return dayjs(item.transaction?.transactionDate?.date).isAfter(dateLimit);
};

export const soldActions = (item, listViewType, memberId) => {
    const common = commonActions(item);
    const actions = [
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Ändra fraktpris',
            getTranslatedLabel: (t) =>
                item.haveCombinedShipping && item.haveMultipleRows
                    ? item.transaction.shippingDiscount
                        ? t(
                              'myt_itemCard_actions_update_combinedshipping_change'
                          )
                        : t('myt_itemCard_actions_update_combinedshipping')
                    : t('myt_itemCard_actions_update_shipping'),
            dispatch: (dispatch) => {
                dispatch(
                    openChangeShipping({
                        purchaseOrderId: item?.transaction?.purchaseOrderId,
                        receiverCountry:
                            item?.transaction?.buyer.address.countryCodeIso2
                    })
                );
            },
            gaLabel: 'ChangeShippingPriceSold',
            isVisible: () =>
                isNormalListViewType(listViewType) &&
                !item.haveMultipleRows &&
                !item.transaction.sellerState.paid,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.LINK,
            label: 'Orderinformation',
            getTranslatedLabel: (t) =>
                t('myt_itemCard_actions_orderInformation'),
            url: `/my/sold/buyer-info?purchaseOrderId=${item.transaction.purchaseOrderId}`,
            gaLabel: 'BuyerInfo',
            isVisible: () =>
                !!item.transaction.purchaseOrderId && !item.haveMultipleRows,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: featureToggles.isReplaceMessagingModalWithLinkEnabled
                ? types.LINK
                : types.DISPATCH,

            url: getMessagingPageLink(
                item.seller.id,
                item.transaction.buyer.id,
                item.itemId,
                {
                    traderaUserId: memberId
                }
            ),
            label: 'Kontakta köpare',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_contactBuyer'),
            dispatch: (dispatch, _props, _preferredLanguageCode, t) => {
                const itemSquareImage = getSrc(
                    item.listing.images.mainTemplate ?? '',
                    '{format}',
                    250
                );
                dispatch(
                    openNewMessageModal({
                        title: t('myt_itemCard_actions_contactBuyer'),
                        itemId: item.itemId,
                        senderId: item.seller.id,
                        recipientId: item.transaction.buyer.id,
                        recipientAlias: item.transaction.buyer.alias,
                        itemTitle: item.listing.shortDescription,
                        itemImageUrl: itemSquareImage,
                        itemSellerId: item.seller.id,
                        purchaseOrderId: item.transaction.purchaseOrderId
                    })
                );
            },
            gaLabel: 'ContactBuyer',
            isVisible: () => {
                return (
                    !item.haveMultipleRows &&
                    featureToggles.isMessagingEnabled &&
                    item.transaction.purchaseOrderId &&
                    item.transaction.buyer
                );
            },
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Markera som betald',
            buttonVariant: 'outline',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_markAsPaid'),
            dispatch: (dispatch) =>
                dispatch(
                    myTraderaItemActions.markPurchaseOrdersAsPaidBySeller(
                        [item.transaction.purchaseOrderId],
                        listViewType
                    )
                ),
            gaLabel: 'MarkAsPaid',
            isVisible: () => {
                return (
                    !item.haveMultipleRows &&
                    [
                        PaidStatuses.UNPAID,
                        PaidStatuses.MARKED_AS_PAID_BY_BUYER
                    ].includes(item.transaction.paidStatus)
                );
            },
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.LINK,
            label: 'Köp frimärke',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_buyStamp'),
            url: `/my/shipping?purchaseOrderId=${item.transaction.purchaseOrderId}&originUrl=%2fmy%2fsold`,
            /*
             * @todo
             */
            gaLabel: 'BookShipping',
            isVisible: () => {
                if (
                    !hasStampsAsSelectedShippingType(item) ||
                    item.haveMultipleRows ||
                    item.transaction.sellerState.shippingBooked ||
                    item.transaction.sellerState.shipped ||
                    item.transaction.hasUnpaidShipping
                ) {
                    return false;
                }
                return true;
            },
            isPrimaryAction: () => {
                return (
                    !item.transaction.hasUnpaidShipping &&
                    !isTakeAway(item) &&
                    hasStampsAsSelectedShippingType(item)
                );
            }
        }),
        () => ({
            id: getId(item),
            type: types.LINK,
            label: 'Boka frakt',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_bookShipping'),
            url: `/my/shipping?purchaseOrderId=${item.transaction.purchaseOrderId}&originUrl=%2fmy%2fsold`,
            /*
             * @todo
             */
            gaLabel: 'BookShipping',
            isVisible: () => {
                const countryCode = item.seller?.address?.countryCodeIso2;
                const hasIntegratedShipping =
                    isSwedenCountryCodeOrUndefined(countryCode) ||
                    isDenmarkCountryCode(countryCode) ||
                    isNetherlandsCountryCode(countryCode);
                if (
                    hasStampsAsSelectedShippingType(item) ||
                    item.haveMultipleRows ||
                    item.transaction.sellerState.shippingBooked ||
                    item.transaction.sellerState.shipped ||
                    item.transaction.hasUnpaidShipping ||
                    !hasIntegratedShipping
                ) {
                    return false;
                }
                return true;
            },
            isPrimaryAction: () => {
                return !item.transaction.hasUnpaidShipping && !isTakeAway(item);
            }
        }),
        () => ({
            id: getId(item),
            type: types.LINK,
            label: 'Betala frakt',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_payShipping'),
            url: item.transaction.unpaidShippingUrl || `/my/shipping/checkout`,
            gaLabel: 'PayShipping',
            isVisible: () => item.transaction.hasUnpaidShipping,
            isPrimaryAction: () => item.transaction.hasUnpaidShipping
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Avbryt fraktbokning',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_deleteShipping'),
            dispatch: (dispatch) => {
                dispatch(
                    myTraderaItemActions.sellingDeleteShipment(
                        item.transaction.unpaidShippingId,
                        item.transaction.purchaseOrderId,
                        listViewType
                    )
                );
            },
            gaLabel: 'DeleteShipping',
            isVisible: () =>
                item.transaction.hasUnpaidShipping && !item.haveMultipleRows,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Avmarkera som bokad',
            getTranslatedLabel: (t) =>
                t('myt_itemCard_actions_unmarkAsShippingBooked'),
            dispatch: (dispatch) => {
                dispatch(
                    myTraderaItemActions.sellingUnmarkAsShippingBooked(
                        [item.transaction.purchaseOrderId],
                        listViewType,
                        [item.transaction.transactionId]
                    )
                );
            },
            gaLabel: 'UnmarkAsShippingBooked',
            isVisible: () => {
                return (
                    !item.transaction.trackingLink &&
                    !item.haveMultipleRows &&
                    item.transaction.sellerState.shippingBooked &&
                    !item.transaction.sellerState.shipped
                );
            },
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Markera som bokad',
            getTranslatedLabel: (t) =>
                t('myt_itemCard_actions_markAsShippingBooked'),
            dispatch: (dispatch) => {
                dispatch(
                    myTraderaItemActions.sellingMarkAsShippingBooked(
                        item.transaction.purchaseOrderId,
                        listViewType,
                        [item.transaction.transactionId]
                    )
                );
            },
            gaLabel: 'MarkAsShippingBooked',
            isVisible: () => {
                return (
                    !item.transaction.trackingLink &&
                    !item.haveMultipleRows &&
                    !item.transaction.sellerState.shippingBooked &&
                    !item.transaction.sellerState.shipped
                );
            },
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.LINK,
            label: 'Spåra paket',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_trackShipment'),
            url: `/my/sold/buyer-info?purchaseOrderId=${item.transaction.purchaseOrderId}#shipping`,
            gaLabel: 'TrackShipment',
            isVisible: () =>
                !item.haveMultipleRows && !!item.transaction.trackingLink,
            isPrimaryAction: () => false
        }),
        () => {
            const isShippingBooked =
                item.transaction.sellerState.shippingBooked;

            const hasShipmentQrCode =
                item.transaction.bookedShipment?.qrCodeImageStatus === 'Done';
            const hasPendingShipmentQrCode =
                item.transaction.bookedShipment?.qrCodeImageStatus ===
                'Pending';
            const hasLabellessShippingCode =
                !!item.transaction.bookedShipment?.labellessShippingCode;

            const getLabel = (t) => {
                if (hasShipmentQrCode) {
                    return t('myt_itemCard_actions_shippingQrCode');
                }

                if (hasPendingShipmentQrCode && hasLabellessShippingCode) {
                    return t('myt_itemCard_actions_shippingQrCode');
                }

                if (hasPendingShipmentQrCode && !hasLabellessShippingCode) {
                    return t('myt_itemCard_actions_shippingQrCode_pending');
                }

                if (hasLabellessShippingCode) {
                    return t('myt_itemCard_actions_shippingLabellessCode');
                }
            };

            return {
                id: getId(item),
                type:
                    hasPendingShipmentQrCode && !hasLabellessShippingCode
                        ? types.DISABLED
                        : types.DISPATCH,
                label: 'Visa QR-kod för frakt',
                getTranslatedLabel: getLabel,
                gaLabel: 'ShowShippingQrCode',
                isVisible: () =>
                    isShippingBooked &&
                    isNormalListViewType(listViewType) &&
                    (hasShipmentQrCode ||
                        hasPendingShipmentQrCode ||
                        hasLabellessShippingCode),
                isPromotedSecondaryAction: () =>
                    !item.transaction.sellerState.shipped,
                isPrimaryAction: () => false,
                dispatch: (dispatch) => {
                    dispatch(
                        openShippingQrCodeModal({
                            purchaseOrderId: item?.transaction?.purchaseOrderId
                        })
                    );
                }
            };
        },
        () => ({
            id: getId(item),
            type: types.EXTERNAL_LINK,
            label: 'Kvitto för frakt',
            getTranslatedLabel: (t) =>
                t('myt_itemCard_actions_shippingReceipt'),
            url: `/my/shipping/receipt/${item.transaction.paidShipmentCheckoutId}`,
            gaLabel: 'ShippingReceipt',
            isVisible: () =>
                item.transaction.bookedShipment &&
                !item.haveMultipleRows &&
                item.transaction.bookedShipment.hasShipmentReceipt,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.EXTERNAL_LINK,
            label: 'Ladda ner fraktsedel',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_shippingLabel'),
            url: `/my/shipping/label/${item.transaction.shipmentId}`,
            gaLabel: 'ShippingLabel',
            isVisible: () =>
                item.transaction.bookedShipment &&
                !item.haveMultipleRows &&
                item.transaction.bookedShipment.hasShipmentLabel,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: item.transaction.bookedShipment?.isSoldByTradera
                ? types.LINK
                : types.EXTERNAL_LINK_ICON,
            label: 'Reklamera frakt',
            getTranslatedLabel: (t) =>
                t('myt_itemCard_actions_shippingComplaint'),
            url: item.transaction.bookedShipment?.isSoldByTradera
                ? `/my/shipping/complaint/${item.transaction.shipmentId}`
                : 'https://portal.postnord.com/claimspublic/sv-SE.html',
            gaLabel: 'ShippingComplaint',
            isVisible: () =>
                item.transaction.bookedShipment &&
                item.transaction.bookedShipment.hasShipmentLabel &&
                !item.haveMultipleRows &&
                item.transaction.bookedShipment.shippingProvider ===
                    SHIPPING_PROVIDERS.POSTNORD_PARCEL,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: isTakeAway(item)
                ? 'Avmarkera som hämtad'
                : 'Avmarkera som skickad',
            getTranslatedLabel: (t) =>
                isTakeAway(item)
                    ? t('myt_itemCard_actions_unmarkAsCollected')
                    : t('myt_itemCard_actions_unmarkAsSent'),
            dispatch: (dispatch) => {
                dispatch(
                    myTraderaItemActions.sellingUnmarkAsShipped(
                        [item.transaction.transactionId],
                        listViewType
                    )
                );
            },
            gaLabel: 'UnmarkAsShipped',
            isVisible: () =>
                item.transaction.sellerState.shipped && !item.haveMultipleRows,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: isTakeAway(item)
                ? 'Markera som hämtad'
                : 'Markera som skickad',
            getTranslatedLabel: (t) =>
                isTakeAway(item)
                    ? t('myt_itemCard_actions_markAsCollected')
                    : t('myt_itemCard_actions_markAsSent'),
            dispatch: (dispatch) => {
                dispatch(
                    myTraderaItemActions.sellingMarkAsShipped(
                        [item.transaction.transactionId],
                        listViewType
                    )
                );
            },
            gaLabel: 'MarkAsShipped',
            isVisible: () =>
                !item.transaction.sellerState.shipped && !item.haveMultipleRows,
            isPromotedSecondaryAction: () =>
                isPaidShippingSetWithPostNordStamp(item) ||
                isPaidSingleItemWithOnlyPostNordStamp(item),
            isPrimaryAction: () => !item.transaction.hasUnpaidShipping
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Lämna omdöme',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_leaveFeedback'),
            dispatch: (dispatch) => {
                dispatch(
                    openModal({
                        modalId: SINGLE_FEEDBACK_DIALOG_ID,
                        data: {
                            transactionId: item.transaction.transactionId
                        }
                    })
                );
            },
            gaLabel: 'LeaveFeedbackOnBuyer',
            isVisible: () =>
                !item.transaction.sellerState.feedbackLeft &&
                !item.haveMultipleRows &&
                item.transaction.paidStatus !== PaidStatuses.PAYMENT_PENDING,
            isPrimaryAction: () => !item.haveMultipleRows
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Återbetala',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_refund'),
            dispatch: (dispatch) =>
                dispatch(openSetDiscount(item.transaction.purchaseOrderId)),
            gaLabel: 'Refund',
            isVisible: () =>
                !item.haveMultipleRows &&
                (item.transaction.isDiscountAfterPaymentSupported ||
                    item.transaction.isRefundAllowed),
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.LINK,
            label: 'Köparens omdömen',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_buyerFeedback'),
            url: `/profile/feedback/${item.transaction.buyer.id}`,
            gaLabel: 'BuyerProfile',
            isVisible: () => !item.haveMultipleRows,
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.LINK,
            label: 'Se köphistorik',
            getTranslatedLabel: (t) =>
                t('myt_itemCard_actions_purchaseHistory'),
            url: `/my/item/${item.itemId}/purchasehistory`,
            gaLabel: 'PurchaseHistory',
            isVisible: () =>
                !(
                    item.listing.itemType === 'Auction' ||
                    item.listing.itemType === 'AuctionBin' ||
                    item.haveMultipleRows
                ),
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Avmarkera som betald',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_unmarkAsPaid'),
            dispatch: (dispatch) =>
                dispatch(
                    myTraderaItemActions.unmarkPurchaseOrdersAsPaidBySeller(
                        [item.transaction.purchaseOrderId],
                        listViewType
                    )
                ),
            gaLabel: 'UnmarkAsPaid',
            isVisible: () => {
                return (
                    item.transaction.paidStatus ===
                        PaidStatuses.MARKED_AS_PAID_BY_SELLER &&
                    !item.haveMultipleRows
                );
            },
            isPrimaryAction: () => true
        }),
        () => ({
            // "UPI" = "Unpaid Item"
            id: getId(item),
            type: types.LINK,
            label: 'Anmäl utebliven betalning',
            getTranslatedLabel: (t) =>
                t('myt_itemCard_actions_reportUnpaidItem'),
            url: isNormalListViewType(listViewType)
                ? `/resolution/UPI2/${item.transaction.purchaseOrderId}`
                : `/resolution/UPI/${item.transaction.transactionId}`,
            gaLabel: 'ReportUnpaidItem',
            isVisible: () =>
                !item.haveMultipleRows &&
                item.transaction.sellerState.state === 'NotPaid' &&
                !item.transaction.sellerState.upiSubmitted &&
                !featureToggles.isDiscontinuedSaleEnabled &&
                hasTransactionDateNewerThen60Days(item) &&
                item.transaction.paidStatus !== PaidStatuses.PAYMENT_PENDING,
            isPrimaryAction: () => true
        }),
        () => ({
            // "DCS" = "Discontinued Sale"
            id: getId(item),
            type: types.LINK,
            label: 'Utebliven försäljning',
            getTranslatedLabel: (t) =>
                t('myt_itemCard_actions_reportDiscontinuedSale'),
            url: `/resolution/DCS/${item.transaction.purchaseOrderId}`,
            gaLabel: 'ReportDiscontinuedSale',
            isVisible: () =>
                !item.haveMultipleRows &&
                featureToggles.isDiscontinuedSaleEnabled &&
                item.transaction.sellerState.state === 'NotPaid' &&
                !item.transaction.sellerState.isMcpSubmitted &&
                !item.transaction.sellerState.isUpiSubmitted &&
                hasTransactionDateNewerThen60Days(item) &&
                item.transaction.paidStatus !== PaidStatuses.PAYMENT_PENDING,
            isPrimaryAction: () => true
        }),
        common.sellSimilar,
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Lämna rabatt',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_giveDiscount'),
            dispatch: (dispatch) =>
                dispatch(openSetDiscount(item.transaction.purchaseOrderId)),
            gaLabel: 'Discount',
            isVisible: () =>
                !item.haveMultipleRows &&
                (item.transaction.isDiscountBeforePaymentSupported ||
                    item.transaction.isDiscountAllowed),
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Ångra dölj',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_unhide'),
            dispatch: (dispatch) => {
                dispatch(
                    isNormalListViewType(listViewType)
                        ? purchaseOrderActions.updatePurchaseOrderHiddenForSeller(
                              item.transaction.purchaseOrderId,
                              false,
                              listViewType
                          )
                        : myTraderaItemActions.sellerUnhideTransaction(
                              [item.transaction.purchaseOrderId],
                              listViewType
                          )
                );
            },
            gaLabel: 'UnhideSold',
            isVisible: () =>
                isNormalListViewType(listViewType)
                    ? !item.haveMultipleRows &&
                      item.transaction?.sellerState.hidden
                    : item.transaction && item.transaction.sellerState.hidden,
            isPrimaryAction: () => true
        })
    ];

    const hide = () => ({
        id: getId(item),
        type: types.DISPATCH,
        label: 'Dölj',
        getTranslatedLabel: (t) => t('myt_itemCard_actions_hide'),
        dispatch: (dispatch) => {
            dispatch(
                isNormalListViewType(listViewType)
                    ? purchaseOrderActions.updatePurchaseOrderHiddenForSeller(
                          item.transaction.purchaseOrderId,
                          true,
                          listViewType
                      )
                    : myTraderaItemActions.sellerHideTransaction(
                          [item.transaction.purchaseOrderId],
                          listViewType
                      )
            );
        },
        gaLabel: 'HideSold',
        isVisible: () =>
            isNormalListViewType(listViewType)
                ? !item.haveMultipleRows &&
                  !item.transaction?.sellerState.hidden
                : item.transaction && !item.transaction.sellerState.hidden,
        isPrimaryAction: () => true
    });

    if (item.transaction && item.transaction.sellerState.state === 'Done') {
        // Make "hide" the primary action by setting it as the first element in the array
        actions.unshift(hide);
    } else {
        actions.push(hide);
    }

    return actions;
};

export const activeSellerActions = (item) => {
    const isShopItem = item.listing.itemType === 'Shopitem';
    const common = commonActions(item);

    const translationKeys = {
        shareHeader: 'myt_shareHeader',
        shareMailSubject: 'myt_shareViaMailSubject',
        shareMailBody: 'myt_shareViaMailBody',
        sharePinterestDescription: 'myt_shareViaPinterestDescription',
        shareLinkText: 'myt_shareLink'
    };

    const actions = [
        () => ({
            id: getId(item),
            type: isShopItem ? types.DISABLED : types.LINK,
            showTooltip: isShopItem,
            getTranslatedTooltip: (t) => t('myt_itemCard_shopItem_tooltip'),
            label: 'Ändra',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_update'),
            url: `/selling/edit/${item.itemId}`,
            gaLabel: isShopItem
                ? 'EditShopItem'
                : item.bidInformation.bidCount === 0
                  ? 'EditItem'
                  : 'AddText',
            isVisible: () => true,
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Dela',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_share'),
            gaLabel: 'ShareActiveSellingItem',
            isVisible: () => true,
            isPrimaryAction: () => false,
            dispatch: (dispatch, _props, preferredLanguageCode) => {
                const shareLinkHref = toLocalizedUrl(
                    new URL(item.itemUrl, window.location.origin).href,
                    preferredLanguageCode
                );
                dispatch(
                    openShareModal({
                        shareLinkHref,
                        translationKeys,
                        shareTitle: item.listing.shortDescription,
                        shareImageUrl: item.listing.images.main.full
                    })
                );
            }
        }),
        common.sellSimilar,
        () => ({
            id: getId(item),
            type: featureToggles.isReplaceMessagingModalWithLinkEnabled
                ? types.LINK
                : types.DISPATCH,
            url: getMessagingPageLink(
                item.seller.id,
                item.bidInformation?.leadingBidder?.id,
                item.itemId,
                {
                    traderaUserId: item.seller.id
                }
            ),
            label: 'Kontakta högstbjudande',
            getTranslatedLabel: (t) =>
                t('myt_itemCard_actions_contactHighestBidder'),
            dispatch: (dispatch, _props, _preferredLanguageCode, t) => {
                const itemSquareImage = getSrc(
                    item.listing.images.mainTemplate ?? '',
                    '{format}',
                    250
                );
                dispatch(
                    openNewMessageModal({
                        title: t('myt_itemCard_actions_contactHighestBidder'),
                        itemId: item.itemId,
                        senderId: item.seller.id,
                        recipientId: item.bidInformation.leadingBidder.id,
                        recipientAlias: item.bidInformation.leadingBidder.alias,
                        itemTitle: item.listing.shortDescription,
                        itemImageUrl: itemSquareImage,
                        itemSellerId: item.seller.id
                    })
                );
            },
            gaLabel: 'ContactHighestBidder',
            isVisible: () => {
                return (
                    featureToggles.isMessagingEnabled &&
                    item.bidInformation.bidCount > 0
                );
            },
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Avbryt annons',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_cancelItem'),
            dispatch: (dispatch) => {
                dispatch(myTraderaItemActions.cancelItem(item));
            },
            gaLabel: 'EndItem',
            isVisible: () => item.bidInformation.bidCount === 0,
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.LINK,
            label: 'Marknadsför',
            getTranslatedLabel: (t) => t('upsell_extras_button'),
            url: `/selling/features/${item.itemId}`,
            isVisible: () => true,
            isPromotedSecondaryAction: () => true
        })
    ];
    return actions;
};

export const unsoldActions = (item, listViewType, memberId) => {
    const actions = [
        () => ({
            id: getId(item),
            type: types.LINK,
            label: item.listing.restart.hasRestartedChild
                ? 'Sälj liknande'
                : 'Lägg upp igen',
            getTranslatedLabel: (t) =>
                item.listing.restart.hasRestartedChild
                    ? t('myt_itemCard_actions_sellSimilar')
                    : t('myt_itemCard_actions_restart'),
            url: item.listing.restart.hasRestartedChild
                ? `/selling/sell-similar/${item.itemId}`
                : `/selling/restart/${item.itemId}`,
            gaLabel: 'Restart',
            isVisible: () => true,
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Dölj',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_hide'),
            dispatch: (dispatch) => {
                dispatch(
                    myTraderaItemActions.sellerHideUnsold(
                        getIds(item),
                        listViewType
                    )
                );
            },
            gaLabel: 'HideUnsold',
            isVisible: () =>
                !item.transaction && !item.hiddenByMember?.includes(memberId),
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Visa',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_show'),
            dispatch: (dispatch) => {
                dispatch(
                    myTraderaItemActions.sellerUnhideUnsold(
                        getIds(item),
                        listViewType
                    )
                );
            },
            gaLabel: 'UnhideUnsold',
            isVisible: () =>
                !item.transaction && item.hiddenByMember?.includes(memberId),
            isPrimaryAction: () => true
        })
    ];
    return actions;
};

const isPaidShippingSetWithPostNordStamp = (item) =>
    item.transaction.sellerState.paid &&
    isStampProvider(item.transaction.selectedShipping?.shippingType);

const isPaidSingleItemWithOnlyPostNordStamp = (item) => {
    return (
        item.transaction.sellerState.paid &&
        item.listing.shippingOptions?.length &&
        item.listing.shippingOptions.every(
            (x) =>
                isStampProvider(x.shippingType) ||
                x.shippingType === SHIPPING_PROVIDERS.TAKE_AWAY
        )
    );
};
